import React, { useCallback, useEffect, useState } from 'react';
import { useRecoilValue, useRecoilState, useResetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { Checkbox, Select } from 'antd';
import dayjs from 'dayjs';
import Link from 'next/link';
import UserAvatar from '../../components/ui/UserAvatar';
import DropAvatar from '../../components/ui/DropAvatar';
import {
  connectionAccountState,
  allAdminDataState,
  searchStoreState,
  adminCategoryTab,
} from '../../recoil/atoms';
import { PageTitle } from '../../components/ui/PageTitle';
import { FilterIcon } from '../../lib/Resources';
import { LoadMore } from '../../components/ui/LoadMore';
import { storageService } from '../../lib/db/StorageService';
import { AdminFilterModal } from '../../components/ui/AdminFilterModal';
import { AdminTable } from '../../components/ui/AdminTable';
import { middleEllipsis, sleep } from '../../lib/Utils';
import { AdminConfirmationModal } from '../../components/ui/AdminConfirmationModal';
import { AdminSpecialDropModal } from '../../components/ui/AdminSpecialDropModal';
import AdminStatus from '../../components/ui/AdminStatus';
import { ACCOUNT_TYPE } from '../../lib/constants';
import SelectDropdown from '../../components/icons/SelectDropdown';
import AdminTransactions from '../../components/ui/AdminTransactions';

const { Option } = Select;

const InitialSelectedCategory = { user: null, field: null, title: null };

const AdminPage = () => {
  const { t } = useTranslation();
  const { query, push } = useRouter();
  const currentAccount = useRecoilValue(connectionAccountState);
  const [adminData, setAdminData] = useRecoilState(allAdminDataState);
  const {
    results: { users: searchedUsers, collections: searchedDrops },
    searching,
  } = useRecoilValue(searchStoreState);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(InitialSelectedCategory);
  const [selectedDrop, setSelectedDrop] = useState(null);
  const [selectedCategory, setSelectedCategory] = useRecoilState(
    adminCategoryTab,
  );

  const clearSearchStore = useResetRecoilState(searchStoreState);

  const [selectedAccountType, setSelectedAccountType] = useState('');

  console.log(searchedUsers);

  const loadData = async () => {
    setAdminData({
      ...adminData,
      hasLoaded: false,
    });
    if (selectedCategory === 'drops') {
      const res = await storageService.findAllDrops({
        page: (query?.page || 1) as number,
        filterBy: (query?.filterBy || '') as string,
        walletAddress: (query?.search || '') as string,
      });

      if (res?.drops) {
        setAdminData({
          ...adminData,
          ...res,
          hasLoaded: true,
        });
      }
    } else {
      const res = await storageService.findAllUsers({
        page: (query?.page || 1) as number,
        filterBy: (query?.filterBy || '') as string,
        walletAddress: (query?.search || '') as string,
      });

      if (res?.users) {
        setAdminData({
          ...adminData,
          ...res,
          hasLoaded: true,
        });
      }
    }
  };

  const isManager = () =>
    currentAccount?.userRoles?.some((role) =>
      ['admin', 'moderator'].includes(role),
    );

  const isModerator = () =>
    !currentAccount?.userRoles?.length ||
    currentAccount?.userRoles?.some((role) => role === 'moderator');

  const checkAccess = async () => {
    await sleep(4_000);
    if (!currentAccount.address) return;
    if (!isManager()) {
      push('/');
    } else {
      setTimeout(() => loadData());
    }
  };

  useEffect(() => {
    setFilterModalVisible(false);
    checkAccess();
  }, [query, currentAccount, selectedCategory]);

  const onRowSelected = (record: {
    user: any;
    field: string;
    title: string;
  }) => {
    setSelectedUser({
      ...record,
    });
  };

  const onDropRowSelected = (drop) => {
    if (currentAccount?.userRoles?.includes('admin')) {
      setSelectedDrop({
        ...drop,
        deactivatedAt: dayjs(drop.deactivatedAt).format(),
      });
    }
  };

  const cancelUserConfirmation = () => {
    setSelectedUser({
      ...InitialSelectedCategory,
    });
  };

  const categories = [
    {
      title: t('Users'),
      key: 'users',
    },
    {
      title: t('🌈 Drops'),
      key: 'drops',
    },
    {
      title: t('💲 Transactions'),
      key: 'transactions',
    },
    {
      title: t('⚡ Status'),
      key: 'status',
    },
  ];

  const usersColumns = [
    {
      title: t('USER'),
      dataIndex: 'walletAddress',
      align: 'center',
      width: 180,
      render: (_, record) => (
        <div className="user-address">
          <div
            style={{
              position: 'relative',
              marginRight: '15px',
            }}
          >
            <UserAvatar
              user={record}
              IdenticonSize={40}
              className="user-avatar-container h-30 relative"
            />
          </div>
          <span>{middleEllipsis(record?.walletAddress, 4)}</span>
        </div>
      ),
    },
    {
      title: t('BANNED'),
      dataIndex: 'id',
      align: 'center',
      render: (_, user) => (
        <Checkbox
          checked={user?.banned}
          disabled={isModerator()}
          onChange={() =>
            onRowSelected({
              user,
              field: 'banned',
              title: user?.banned ? t('UNBAN USER') : t('BAN USER'),
            })
          }
        />
      ),
    },
    {
      title: t('MINTING DISABLED'),
      dataIndex: 'id',
      align: 'center',
      render: (_, user) => (
        <Checkbox
          checked={user?.notAllowedToMint}
          onChange={() =>
            onRowSelected({
              user,
              field: 'notAllowedToMint',
              title: user?.notAllowedToMint
                ? t('ALLOW MINTING')
                : t('DISABLE MINTING'),
            })
          }
        />
      ),
    },
    {
      title: t('VERIFIED'),
      dataIndex: 'id',
      align: 'center',
      render: (_, user) => (
        <Checkbox
          checked={user?.verified}
          onChange={() =>
            onRowSelected({
              user,
              field: 'verified',
              title: user?.verified ? t('UNVERIFY') : t('VERIFY'),
            })
          }
        />
      ),
    },
    {
      title: t('LEADERBOARD'),
      dataIndex: 'id',
      align: 'center',
      render: (_, user) => (
        <Checkbox
          checked={user?.leaderboard}
          disabled={isModerator()}
          onChange={() =>
            onRowSelected({
              user,
              field: 'leaderboard',
              title: user?.leaderboard
                ? t('DISABLE LEADERBOARD LISTING')
                : t('ALLOW LEADERBOARD LISTING'),
            })
          }
        />
      ),
    },
    {
      title: t('SPECIAL DROPS'),
      dataIndex: 'id',
      align: 'center',
      render: (_, user) => (
        <Checkbox
          checked={user?.canCreateDrop}
          disabled={isModerator()}
          onChange={() =>
            onRowSelected({
              user,
              field: 'canCreateDrop',
              title: user?.canCreateDrop
                ? t('DISABLE DROP CREATION')
                : t('ALLOW DROP CREATION'),
            })
          }
        />
      ),
    },
    {
      title: t('ACCOUNT TYPE'),
      dataIndex: 'id',
      align: 'center',
      render: (_, user) => (
        <Select
          size="large"
          className="uppercase"
          disabled={isModerator()}
          onChange={(accountType) => {
            setSelectedAccountType(accountType);
            if (accountType !== user.accountType) {
              onRowSelected({
                user,
                field: 'accountType',
                title: t('ACCOUNT TYPE'),
              });
            }
          }}
          defaultValue={user.accountType || 'individual'}
          suffixIcon={SelectDropdown}
        >
          {Object.values(ACCOUNT_TYPE).map((item) => (
            <Option className="uppercase" key={item.name} value={item.name}>
              {item.name}
            </Option>
          ))}
        </Select>
      ),
    },
  ];

  const dropsColumns = [
    {
      title: t('DROP'),
      dataIndex: 'id',
      width: 170,
      render: (_, record) => (
        <Link href={`/drop/${record.dropID}`}>
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <div
              style={{
                position: 'relative',
                marginRight: '10px',
              }}
            >
              <DropAvatar
                drop={{
                  imageUrl: record.imageUrl,
                  imageUrlThumbnail: record.imageUrlThumbnail,
                }}
              />
            </div>
            <div
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '150px',
              }}
            >
              {record?.title}
            </div>
          </div>
        </Link>
      ),
    },
    {
      title: t('ACTIVE'),
      dataIndex: 'id',
      align: 'center',
      render: (_, record) => (
        <Checkbox
          checked={record?.active}
          disabled={isModerator()}
          onChange={() => onDropRowSelected(record)}
        />
      ),
    },
    {
      title: t('PRIORITY'),
      dataIndex: 'id',
      align: 'center',
      render: (_, record) => record.priority,
    },
    {
      title: t('PUBLISHED'),
      dataIndex: 'id',
      align: 'center',
      render: (_, record) => (
        <Checkbox
          disabled={isModerator()}
          checked={record?.published}
          onChange={() => onDropRowSelected(record)}
        />
      ),
    },
    {
      title: t('COLLECTION'),
      dataIndex: 'id',
      align: 'center',
      render: (_, record) => (
        <Checkbox
          disabled={isModerator()}
          checked={record?.collection}
          onChange={() => onDropRowSelected(record)}
        />
      ),
    },
    {
      title: t('DROP ENDS'),
      dataIndex: 'id',
      align: 'center',
      render: (_, record) => dayjs(record.deactivatedAt).format('DD/MM/YYYY'),
    },
  ];

  const onCategoryClicked = (category) => {
    setSelectedCategory(category.key);
  };

  const onFilterClicked = () => {
    if (selectedCategory === 'users') {
      setFilterModalVisible(true);
    }
  };

  const renderPanels = useCallback(() => {
    switch (selectedCategory) {
      case 'drops':
        return (
          <AdminTable
            dataSource={searchedDrops.length ? searchedDrops : adminData.drops}
            columns={dropsColumns}
            loading={searching || !adminData.hasLoaded}
          />
        );

      case 'status':
        return <AdminStatus />;

      case 'transactions':
        return <AdminTransactions />;

      default:
        return (
          <AdminTable
            dataSource={searchedUsers.length ? searchedUsers : adminData.users}
            columns={usersColumns}
            loading={searching || !adminData.hasLoaded}
          />
        );
    }
  }, [
    selectedCategory,
    adminData.drops,
    adminData.users,
    adminData.hasLoaded,
    searchedUsers,
    searchedDrops,
    searching,
  ]);

  return (
    <div className="create-page-content">
      <PageTitle title={t('Admin Dashboard')} />
      <AdminFilterModal
        visible={filterModalVisible}
        onCancel={() => setFilterModalVisible(false)}
      />
      <AdminConfirmationModal
        user={selectedUser.user}
        title={selectedUser.title}
        field={selectedUser.field}
        visible={!!selectedUser.user}
        requestBody={{ accountType: selectedAccountType }}
        requestAction={selectedAccountType ? 'account-type' : null}
        onCancel={cancelUserConfirmation}
        onSuccess={async () => {
          await loadData();
          clearSearchStore();
        }}
      />
      {selectedDrop && (
        <AdminSpecialDropModal
          drop={selectedDrop}
          visible={!!selectedDrop}
          onCancel={() => setSelectedDrop(null)}
        />
      )}
      <div className="nfts-filter-row">
        <ul>
          <li
            key="filter"
            onClick={onFilterClicked}
            onKeyDown={onFilterClicked}
            role="presentation"
            tabIndex={-120}
            className="admin-category-filter"
          >
            <div className="admin-category-text">
              <img
                src={FilterIcon}
                alt="Share Icon"
                height={42}
                width={42}
                role="presentation"
                className=""
              />
            </div>
          </li>

          {categories?.map((category, index) => (
            <li
              className={
                selectedCategory === category.key
                  ? 'nft-category nft-category-selected'
                  : 'nft-category'
              }
              key={category.key}
              onClick={() => onCategoryClicked(category)}
              onKeyDown={() => onCategoryClicked(category)}
              role="presentation"
              tabIndex={index}
            >
              <div className="nft-category-text">{category.title}</div>
            </li>
          ))}
        </ul>
      </div>
      {renderPanels()}

      <LoadMore
        size={0}
        showLoadMore={
          adminData?.usersMeta?.currentPage < adminData?.usersMeta?.totalPages
        }
        onLoadMore={loadData}
      />
    </div>
  );
};

export default AdminPage;
